import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { springs } from "@/components/ui/motion";
import { Dropdown } from "@/components/ui/Dropdown";
import { Input, Textarea } from "@/components/ui/primitives";
import { Button } from "@/components/ui/primitives";
import { useTranslation } from "@/packages/utils";
import type { AuditTemplate } from "@/hooks/use-audit-templates";
import type { WorkspaceAuditType } from "@/hooks/use-workspace-audit-types";

export interface WorkspaceStandardOption {
  _id: string;
  ready: boolean;
  auditTypeName: string;
  auditTypeCode?: string;
  workspaceAuditTypeId?: string;
}

interface AuditTypeSelectorProps {
  templates: AuditTemplate[];
  workspaceTypes: WorkspaceAuditType[];
  workspaceStandards: WorkspaceStandardOption[];
  selectedTemplateId: string;
  selectedWorkspaceTypeId: string;
  sourceStandardId: string | null;
  onChange: (selection: {
    templateId: string;
    workspaceAuditTypeId: string;
    sourceStandardId?: string;
  }) => void;
  onCreateWorkspaceType: (args: {
    name: string;
    code: string;
    description?: string;
  }) => Promise<void>;
}

export function AuditTypeSelector({
  templates,
  workspaceTypes,
  workspaceStandards,
  selectedTemplateId,
  selectedWorkspaceTypeId,
  sourceStandardId,
  onChange,
  onCreateWorkspaceType,
}: AuditTypeSelectorProps) {
  const { t } = useTranslation(["audits", "common"]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formCode, setFormCode] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  const selectedTemplate = templates.find(
    (tmpl) => tmpl.id === selectedTemplateId,
  );
  const selectedWsType = workspaceTypes.find(
    (wsType) => wsType.id === selectedWorkspaceTypeId,
  );
  const selectedStandard = sourceStandardId
    ? workspaceStandards.find((s) => s._id === sourceStandardId)
    : null;

  const selectedLabel = selectedStandard
    ? `${selectedStandard.auditTypeName}${selectedStandard.auditTypeCode ? ` — ${selectedStandard.auditTypeCode}` : ""}`
    : selectedTemplate
      ? `${selectedTemplate.name} — ${selectedTemplate.code}`
      : selectedWsType
        ? `${selectedWsType.name} — ${selectedWsType.code}`
        : t("wizard.selectAuditType");

  useEffect(() => {
    if (showCreateForm) {
      const timer = setTimeout(() => nameRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [showCreateForm]);

  const handleSave = async () => {
    if (!formName.trim() || !formCode.trim() || isSaving) return;
    setIsSaving(true);
    try {
      await onCreateWorkspaceType({
        name: formName.trim(),
        code: formCode.trim().toUpperCase(),
        description: formDesc.trim() || undefined,
      });
      setShowCreateForm(false);
      setFormName("");
      setFormCode("");
      setFormDesc("");
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setShowCreateForm(false);
    }
  };

  return (
    <div>
      <Dropdown>
        <Dropdown.Trigger>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-button border border-border bg-card px-3 py-2 text-[14px] text-left cursor-pointer"
          >
            <span
              className={
                selectedTemplate || selectedWsType || selectedStandard
                  ? "text-primary"
                  : "text-muted"
              }
            >
              {selectedLabel}
            </span>
            <ChevronDown className="h-4 w-4 text-muted" />
          </button>
        </Dropdown.Trigger>
        <Dropdown.Content side="bottom" align="start">
          {workspaceStandards.length > 0 && (
            <>
              <Dropdown.Label>
                {t("common:workspaceStandards.title")}
              </Dropdown.Label>
              {workspaceStandards.map((standard) => (
                <Dropdown.Item
                  key={standard._id}
                  disabled={!standard.ready}
                  onSelect={() => {
                    if (!standard.ready) return;
                    onChange({
                      templateId: "",
                      workspaceAuditTypeId: standard.workspaceAuditTypeId ?? "",
                      sourceStandardId: standard._id,
                    });
                  }}
                >
                  <div className="flex w-full items-center">
                    <div className="min-w-0 flex-1">
                      <div
                        className={
                          standard.ready ? "text-primary" : "text-muted"
                        }
                      >
                        {standard.auditTypeName}
                      </div>
                      {standard.auditTypeCode && (
                        <div className="text-[11px] text-muted">
                          {standard.auditTypeCode}
                        </div>
                      )}
                    </div>
                    {!standard.ready ? (
                      <span className="ml-auto shrink-0 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-500">
                        {t("common:workspaceStandards.badges.notReady")}
                      </span>
                    ) : (
                      <span className="ml-auto shrink-0 rounded-full bg-purple-500/10 px-2 py-0.5 text-[10px] font-medium text-purple-500">
                        {t("common:workspaceStandards.badges.custom")}
                      </span>
                    )}
                  </div>
                </Dropdown.Item>
              ))}
            </>
          )}
          {templates.length > 0 && (
            <>
              <Dropdown.Label>{t("wizard.globalTemplates")}</Dropdown.Label>
              {templates.map((tmpl) => (
                <Dropdown.Item
                  key={tmpl.id}
                  onSelect={() =>
                    onChange({ templateId: tmpl.id, workspaceAuditTypeId: "" })
                  }
                >
                  <div className="flex w-full items-center">
                    <div className="min-w-0 flex-1">
                      <div className="text-primary">{tmpl.name}</div>
                      <div className="text-[11px] text-muted">{tmpl.code}</div>
                    </div>
                    <span className="ml-auto shrink-0 rounded-full bg-green-500/10 px-2 py-0.5 text-[10px] font-medium text-green-500">
                      {t("common:workspaceStandards.badges.official")}
                    </span>
                  </div>
                </Dropdown.Item>
              ))}
            </>
          )}
          {/* {workspaceTypes.length > 0 && (
            <>
              <Dropdown.Label>{t("wizard.workspaceCustom")}</Dropdown.Label>
              {workspaceTypes.map((wsType) => (
                <Dropdown.Item
                  key={wsType.id}
                  onSelect={() =>
                    onChange({
                      templateId: "",
                      workspaceAuditTypeId: wsType.id,
                    })
                  }
                >
                  <div>
                    <div className="text-primary">{wsType.name}</div>
                    <div className="text-[11px] text-muted">{wsType.code}</div>
                  </div>
                </Dropdown.Item>
              ))}
            </>
          )} */}
          <Dropdown.Separator />
          <Dropdown.Item
            onSelect={() => {
              setShowCreateForm(true);
            }}
          >
            {t("wizard.createNewAuditType")}
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown>

      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springs.snappy}
            className="overflow-hidden"
          >
            <div className="mt-2 border border-accent/30 rounded-card overflow-hidden">
              <div className="px-3 py-3 space-y-2">
                <div>
                  <span className="text-[11px] font-medium text-muted uppercase tracking-wider mb-1 block">
                    {t("wizard.auditTypeName")}
                  </span>
                  <Input
                    ref={nameRef}
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t("wizard.auditTypeNamePlaceholder")}
                  />
                </div>
                <div>
                  <span className="text-[11px] font-medium text-muted uppercase tracking-wider mb-1 block">
                    {t("wizard.auditTypeCode")}
                  </span>
                  <Input
                    value={formCode}
                    onChange={(e) =>
                      setFormCode(
                        e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""),
                      )
                    }
                    onKeyDown={handleKeyDown}
                    placeholder={t("wizard.auditTypeCodePlaceholder")}
                    maxLength={10}
                  />
                </div>
                <div>
                  <span className="text-[11px] font-medium text-muted uppercase tracking-wider mb-1 block">
                    {t("wizard.auditTypeDescriptionLabel")}
                  </span>
                  <Textarea
                    value={formDesc}
                    onChange={(e) => setFormDesc(e.target.value)}
                    placeholder={t("wizard.auditTypeDescPlaceholder")}
                    rows={2}
                  />
                </div>
                <div className="flex items-center justify-end gap-1.5 pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCreateForm(false)}
                  >
                    {t("common:actions.cancel")}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSave}
                    loading={isSaving}
                    disabled={!formName.trim() || !formCode.trim()}
                  >
                    {t("wizard.saveAndSelect")}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
